import React from 'react';
import {CommonFeatureServices} from '../contexts/feature-app-environment-context';
import {useLayerContext} from '../contexts/layer-context';
import {useFeatureService} from './use-feature-service';
import {DisclaimerKind, DisclaimerReferenceProps} from '../types';
import {useFeatureAppId} from './use-feature-app-id';
import {useConfigurationsContext} from '../contexts/configuration-context';

export function useDisclaimer(
  text: string,
  renderKind: DisclaimerKind,
  labelTranslation: string | undefined,
  closeButtonLabel: string
): DisclaimerReferenceProps {
  const layerContext = useLayerContext();
  const {externallyRenderedDisclaimerKind} = useConfigurationsContext();
  // we only need a scope if inside a layer
  const scope = layerContext?.disclaimerScope;

  const kind =
    renderKind === DisclaimerKind.AppRendered
      ? 'module-reference'
      : externallyRenderedDisclaimerKind;

  if (
    layerContext &&
    !layerContext.usesFooterDisclaimers &&
    kind === 'footer-reference'
  ) {
    throw new Error(
      'Tried to use a footer reference disclaimer inside a layer, but the layer has [usesFooterDisclaimers=false] and the disclaimer text would never be shown'
    );
  }

  const faId = useFeatureAppId();

  const getReferenceProps = React.useCallback(
    (reference: string): DisclaimerReferenceProps => ({
      kind: renderKind,
      text,
      reference,
      scope,
      disclaimerTextId:
        kind === 'module-reference'
          ? `${faId}/disclaimers/${scope ?? 'default-scope'}/${reference}`
          : undefined,
      ariaLabel: labelTranslation
        ? labelTranslation.replace('%referenceNumber%', reference)
        : '',
      closeButtonAriaLabel: closeButtonLabel,
    }),
    [kind, renderKind, text, scope, faId, labelTranslation, closeButtonLabel]
  );

  const disclaimerManagerService = useFeatureService(
    CommonFeatureServices.disclaimerManager
  );

  const disclaimerManager = React.useMemo(
    () => disclaimerManagerService.getScopedDisclaimerManager(scope),
    [disclaimerManagerService, scope]
  );

  // A little hacky - for synchronous initial render
  const initialDisclaimer: DisclaimerReferenceProps = React.useMemo(
    () =>
      getReferenceProps(
        disclaimerManager.registerReferenceDisclaimer(kind, text)
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- should only be done on first render
    []
  );

  const [disclaimer, setDisclaimer] =
    React.useState<DisclaimerReferenceProps>(initialDisclaimer);
  const isInitial = disclaimer === initialDisclaimer;

  React.useEffect(() => {
    if (!isInitial) {
      setDisclaimer(
        getReferenceProps(
          disclaimerManager.registerReferenceDisclaimer(kind, text)
        )
      );
    }

    return () => {
      disclaimerManager.unregisterReferenceDisclaimer(kind, text);
    };
  }, [isInitial, kind, text, disclaimerManager, getReferenceProps]);

  return disclaimer;
}
